import { useEffect, useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { usePallet } from "../../../contexts/PalletContext";
import { useAlert } from "../../../contexts/AlertContext";
import ConsumoService, { IResponsavelConsumoIA } from "../../../services/ConsumoService";

// Modal "Autorizados" do Consumo IA (só master): quem recebe os e-mails de 80% e de
// limite atingido e pode solicitar legisdots pelo botão "Contratar Legisdots".
const GerUsuariosConsumoIaAutorizados = (props: { open: boolean; onClose: (salvou: boolean) => void }) => {
  const { pallet } = usePallet();
  const { NewAlert } = useAlert();

  const corPrimaria = pallet?.backgroundColorPrimary || "#3B0675";
  const corTexto = pallet?.textColorQuaternary || "#1E0736";

  const [usuarios, setUsuarios] = useState<IResponsavelConsumoIA[]>([]);
  const [marcados, setMarcados] = useState<Set<number>>(new Set());
  const [filtro, setFiltro] = useState("");
  const [carregando, setCarregando] = useState(false);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (!props.open) return;
    setFiltro("");
    setCarregando(true);
    ConsumoService.GetResponsaveisConsumoIA()
      .then((res) => {
        if (!res.ok) {
          NewAlert("error", "Não foi possível carregar os usuários.");
          return;
        }
        setUsuarios(res.data || []);
        setMarcados(new Set((res.data || []).filter((u) => u.responsavel).map((u) => u.id_usuario)));
      })
      .finally(() => setCarregando(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  const visiveis = useMemo(() => {
    const f = filtro.trim().toLowerCase();
    if (!f) return usuarios;
    return usuarios.filter((u) => `${u.nome} ${u.email}`.toLowerCase().includes(f));
  }, [usuarios, filtro]);

  const alternar = (id: number) =>
    setMarcados((prev) => {
      const novo = new Set(prev);
      novo.has(id) ? novo.delete(id) : novo.add(id);
      return novo;
    });

  const salvar = async () => {
    setSalvando(true);
    const res = await ConsumoService.SalvarResponsaveisConsumoIA([...marcados]);
    setSalvando(false);
    if (!res.ok) {
      NewAlert("error", res.mensagem || "Não foi possível salvar os autorizados.");
      return;
    }
    NewAlert("success", "Autorizados atualizados.");
    props.onClose(true);
  };

  return (
    <Dialog
      open={props.open}
      onClose={() => !salvando && props.onClose(false)}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: "10px" } }}
    >
      <DialogTitle
        sx={{
          background: pallet?.backgroundColorTertiary || "#F9F9F9",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontFamily: pallet?.general?.fontFamily,
          color: corTexto,
          fontWeight: 700,
          fontSize: 18,
        }}
      >
        Autorizados · Consumo IA
        <IconButton onClick={() => props.onClose(false)} size="small" disabled={salvando}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Typography sx={{ fontSize: 13, color: "#555", mb: 2, fontFamily: pallet?.general?.fontFamily }}>
          Os usuários marcados recebem os avisos por e-mail quando o consumo de legisdots atinge
          80% e quando o limite do mês é atingido, e podem solicitar legisdots.
        </Typography>

        <TextField
          size="small"
          fullWidth
          placeholder="Buscar por nome ou e-mail"
          value={filtro}
          onChange={(e) => setFiltro(e.target.value)}
          sx={{ mb: 1.5 }}
        />

        {carregando ? (
          <Grid container justifyContent="center" sx={{ py: 4 }}>
            <CircularProgress size={28} sx={{ color: corPrimaria }} />
          </Grid>
        ) : (
          <Grid sx={{ maxHeight: 360, overflowY: "auto" }}>
            {visiveis.map((u) => (
              <Grid
                key={u.id_usuario}
                container
                alignItems="center"
                wrap="nowrap"
                onClick={() => alternar(u.id_usuario)}
                sx={{ py: 0.5, borderBottom: "1px solid #f0f0f0", cursor: "pointer" }}
              >
                <Checkbox
                  size="small"
                  checked={marcados.has(u.id_usuario)}
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => alternar(u.id_usuario)}
                  sx={{ color: corPrimaria, "&.Mui-checked": { color: corPrimaria } }}
                />
                <Grid sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13, color: corTexto, fontWeight: 600 }} noWrap>
                    {u.nome}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: "#7a7a7a" }} noWrap>
                    {u.email}
                  </Typography>
                </Grid>
              </Grid>
            ))}
            {!visiveis.length && (
              <Typography sx={{ fontSize: 13, color: "#7a7a7a", py: 2, textAlign: "center" }}>
                Nenhum usuário encontrado.
              </Typography>
            )}
          </Grid>
        )}
      </DialogContent>

      <DialogActions sx={{ background: pallet?.backgroundColorTertiary || "#F9F9F9", px: 3, py: 2 }}>
        <Typography sx={{ fontSize: 12, color: "#7a7a7a", mr: "auto" }}>
          {marcados.size} selecionado(s)
        </Typography>
        <Button
          onClick={() => props.onClose(false)}
          disabled={salvando}
          sx={{ textTransform: "none", color: corTexto, fontFamily: pallet?.general?.fontFamily }}
        >
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={salvar}
          disabled={salvando || carregando}
          sx={{
            textTransform: "none",
            background: corPrimaria,
            fontFamily: pallet?.general?.fontFamily,
            fontWeight: 600,
            "&:hover": { background: pallet?.textColorTertiary || "#4B1196" },
          }}
        >
          {salvando ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Salvar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GerUsuariosConsumoIaAutorizados;
